/* eslint-disable react-hooks/exhaustive-deps */
import { Group, LastDataSubscriptionResult, Thing, ThingDataSubscriptionInfo, ThingType } from '@eagle/core-data-types';
import {
  AppliedFilter,
  AppliedFilterType,
  CacheDataTypes,
  entityGroup,
  ErrorPage,
  evaluate,
  FetchOneOfAll,
  FILTER_OUT,
  FilterDataTypes,
  filterDeletedCache,
  FilterFieldNewProps,
  FilterPathIdentifiers,
  filterToQuery,
  FilterTypes,
  FindItemsDeferredResult,
  getListResultDescription,
  Icon,
  ListPage,
  ListPageQuery,
  MiddleSpinner,
  NEW_FILTER_FLAG,
  NewFilter,
  ReplacePath,
  SAVED_FILTER_KEY,
  SearchResults,
  T_MANY,
  T_ONE,
  testid,
  THING_FILTER_STORAGE_KEY,
  THING_LIST_DEFAULT_TEMPLATE,
  TRACK_THING_LIST_ROW_DEFAULT_TEMPLATE,
  trackEvent,
  Undefinable,
  useAuthenticated,
  useCustomRoutes,
  useFetchAllCache,
  useFlags,
  usePromise,
  useThingDataSubscriptionInfo,
  useUiTemplate
} from '@eagle/react-common';
import Axios from 'axios';
import type { TFunction } from 'i18next';
import { FC, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface ThingListItemProps {
  thing: Thing;
  thingType: Undefinable<ThingType>;
  template: unknown;
  lastDataSubscription?: LastDataSubscriptionResult | null;
}

interface ItemsWithSubscription extends Thing {
  lastDataSubscription?: LastDataSubscriptionResult;
}

interface GetItemsWithSubscriptionParams {
  items: Thing[];
  subscriptions: Record<string, ThingDataSubscriptionInfo | null> | undefined;
}

const ThingListItem: FC<ThingListItemProps> = ({ thing, thingType, template, lastDataSubscription }) => {
  return <>{evaluate(template, { thing, thingType, lastDataSubscription })}</>;
};

const thingListItemRenderer = (thing: Thing, thingType: Undefinable<ThingType>, template: unknown, lastDataSubscription?: LastDataSubscriptionResult | null): JSX.Element => (
  <ThingListItem thing={thing} thingType={thingType} template={template} lastDataSubscription={lastDataSubscription} />
);

const FILTER_REPLACE_PATHS: ReplacePath[] = [
  {
    old: FilterTypes.THING,
    new: FilterTypes.ID,
  },
];

interface Sort {
  field: string;
  direction: string;
  type?: 'property' | 'sharedProperty';
}

interface ThingListContentProps {
  highlightIndex: Undefinable<number>;
  isLoading: boolean;
  items: Thing[] | undefined;
  text: string;
  dataGridEnabled: boolean;
  listTemplate: unknown;
  onSort: (model: Sort) => void;
  sort: Sort;
  template: unknown;
  t: TFunction;
  buildHref: (item: Thing) => string;
}

const ThingListContent: FC<ThingListContentProps> = ({
  highlightIndex,
  isLoading,
  items,
  text,
  dataGridEnabled,
  listTemplate,
  onSort,
  sort,
  template,
  t,
  buildHref,
}) => {
  const thingIds = useMemo(
    () => items?.map((item) => item._id).filter(Boolean) ?? [],
    [items]
  );
  const [subscriptions] = useThingDataSubscriptionInfo(thingIds);

  if (dataGridEnabled) {
    if (!items?.length) {
      return <>{evaluate(listTemplate, { items: [], onSort, sort, loading: isLoading })}</>;
    }

    const itemsWithLastDataSubscription = getItemsWithLastDataSubscription({ items, subscriptions });

    return <>{evaluate(listTemplate, { items: itemsWithLastDataSubscription, onSort, sort, loading: isLoading })}</>;
  }

  return <SearchResults<Thing>
    data-testid='thing-search-results'
    id='thing'
    buildHref={buildHref}
    getItemTestId={(item) => testid`thing-${item._id}`}
    handleFormatListItem={(item) => {
      const dataSubscriptionInfo = item._id ? subscriptions?.[item._id] : undefined;
      return <FetchOneOfAll
        dataType={CacheDataTypes.THING_TYPE}
        id={item.thingTypeId}
        notFoundFactory={() => thingListItemRenderer(item, undefined, template, dataSubscriptionInfo?.lastDataSubscription)}
        renderFactory={(thingType: ThingType) => thingListItemRenderer(item, thingType, template, dataSubscriptionInfo?.lastDataSubscription)}
      />;
    }}
    highlightIndex={highlightIndex}
    initialInstructions={t('common:component.lookup.hint.initial')}
    isLoading={isLoading}
    noResultsInstructions={t('common:page.thing-list.hint.none')}
    searchResults={items}
    selectedItem={null}
    text={text}
  />;
};

const buildSortField = (sort: Sort, featureFlagEnabled: boolean): Record<string, string> => {
  if (!featureFlagEnabled) {
    return { display: 'asc' };
  }

  switch (sort.type) {
    case 'sharedProperty':
      return { [`sharedProperties.${sort.field}.value`]: sort.direction };
    case 'property':
      return { [`properties.${sort.field}.value`]: sort.direction };
    default:
      return { [sort.field]: sort.direction };
  }
};

const getItemsWithLastDataSubscription = ({ items, subscriptions }: GetItemsWithSubscriptionParams): ItemsWithSubscription[] =>
  items.map((item) => ({
    ...item,
    lastDataSubscription: subscriptions?.[item._id]?.lastDataSubscription ?? undefined,
  }));

export const ThingList: FC = () => {
  const { t } = useTranslation(['common', 'track', 'terms']);
  const { thing: customRoutesThing } = useCustomRoutes();
  const navigate = useNavigate();
  const { account, restClient } = useAuthenticated();
  const flags = useFlags();

  const { template, loaded: templateLoaded } = useUiTemplate('track-thing-list-row', TRACK_THING_LIST_ROW_DEFAULT_TEMPLATE);
  const { template: listTemplate, loaded: listTemplateLoaded } = useUiTemplate('track-thing-list', THING_LIST_DEFAULT_TEMPLATE);
  const [sort, setSort] = useState<Sort>({ field: 'display', direction: 'asc' });

  const groupCache = useFetchAllCache(CacheDataTypes.GROUP);
  const [groups, groupError, groupState] = usePromise(
    filterDeletedCache<Group>(groupCache),
    [account, groupCache],
  );

  const thingTypesCache = useFetchAllCache(CacheDataTypes.THING_TYPE);
  const [thingTypes, thingTypesError, thingTypesState] = usePromise(
    filterDeletedCache<ThingType>(thingTypesCache),
    [thingTypesCache],
  );

  const dataGridEnabled = Boolean(flags['track-list-pages-data-grid-feature-temporary']);

  const findThings = ({ filters, pagination, search }: ListPageQuery): FindItemsDeferredResult<Thing> => {
    const cancelToken = Axios.CancelToken.source();
    const sortField = buildSortField(sort, dataGridEnabled);

    return {
      cancel: () => cancelToken.cancel(),
      promise: restClient.thing.getAllV1Paged({
        ...pagination,
        ...(search ? { search } : {}),
        filter: { ...filterToQuery(filters, FILTER_REPLACE_PATHS), ...FILTER_OUT.deleted },
        sort: !search ? JSON.stringify(sortField) : undefined,
      }, { cancelToken: cancelToken.token }).then((response) => {
        const matchCount = response.count;
        const resultDescription = getListResultDescription({ count: matchCount, entityKey: 'terms:thing', filters, search, t });

        if (search) {
          trackEvent('keyword_search', 'returned_results', 'thing_list', { 'results_type': 'thing', 'results_count': matchCount, 'search_term': search });
        }

        return {
          result: {
            results: response.entities,
            itemCount: matchCount,
          },
          resultDescription,
        };
      }),
    };
  };

  const navigateToThing = (thing: Thing): void => {
    navigate(`/${customRoutesThing}/${encodeURIComponent(thing._id)}`);
  };

  const buildHref = (item: Thing): string => {
    return `/${customRoutesThing}/${encodeURIComponent(item._id)}`;
  };

  const renderFilterContent = (
    filters: AppliedFilter<AppliedFilterType>[],
    setFilterOpen: (value: boolean) => void,
    onFiltersChanged: (filters: AppliedFilter<AppliedFilterType>[]) => unknown,
  ): JSX.Element => {
    const filterFields: FilterFieldNewProps[] = [
      {
        apiUrl: '/api/v1/thing',
        attributes: {
          typeCache: thingTypesCache,
          typePath: FilterTypes.THING_TYPE,
        },
        dataType: FilterDataTypes.API,
        fieldLabel: t('common:component.filter.labels.select-a-thing'),
        pathIdentifier: FilterPathIdentifiers.THING,
        propertyLabel: t('terms:thing', { count: T_MANY }),
        typePropertyName: FilterTypes.THING,
      },
      {
        dataType: FilterDataTypes.CACHE,
        entityCache: groupCache,
        fieldLabel: t('common:component.filter.labels.select-a-group'),
        pathIdentifier: FilterPathIdentifiers.GROUP,
        propertyLabel: t('common:terms.group', { count: T_MANY }),
        typePropertyName: FilterTypes.GROUP,
      },
    ];

    return (
      <NewFilter
        filterFields={filterFields}
        filters={filters}
        onCloseClicked={() => setFilterOpen(false)}
        onFiltersChanged={onFiltersChanged}
        savedFilterKey={SAVED_FILTER_KEY}
        storageKey={THING_FILTER_STORAGE_KEY}
        data-testid="things-new-filter"
      />
    );
  };

  if (thingTypesState === 'pending' || groupState === 'pending' || !templateLoaded || !listTemplateLoaded) return <MiddleSpinner />;
  if (groupError) return <ErrorPage error={groupError} />;
  if (thingTypesError) return <ErrorPage error={thingTypesError} />;

  const onSort = (model: Sort): void => {
    setSort(model);
  };

  return (
    <ListPage<Thing>
      data-testid='thing-list'
      dataExportOverrides={{
        getFilterString: (filters) => JSON.stringify({ ...filterToQuery(filters, FILTER_REPLACE_PATHS), ...FILTER_OUT.deleted }),
        getSort: () => JSON.stringify(buildSortField(sort, dataGridEnabled)),
      }}
      id='thing'
      filterFields={[
        {
          entityTypes: thingTypes ?? [],
          propertyLabel: t('common:common.labels.type'),
          typePropertyName: FilterTypes.THING_TYPE,
        },
        {
          entityTypes: groups ? entityGroup(groups) : [],
          propertyLabel: t('common:terms.group', { count: T_ONE }),
          typePropertyName: FilterTypes.GROUP,
        },
      ]}
      icon={<Icon icon="ThingIcon" />}
      navigateToEntity={navigateToThing}
      onQueryChanged={findThings}
      pathIdentifiers={[FilterPathIdentifiers.THING, FilterPathIdentifiers.GROUP]}
      containerSize='xl'
      renderContent={(highlightIndex, isLoading, items, text) => {
        return <ThingListContent
          highlightIndex={highlightIndex}
          isLoading={isLoading}
          items={items}
          text={text}
          dataGridEnabled={dataGridEnabled}
          listTemplate={listTemplate}
          onSort={onSort}
          sort={sort}
          template={template}
          t={t}
          buildHref={buildHref}
        />;
      }}
      renderFilterContent={flags[NEW_FILTER_FLAG] ? renderFilterContent : undefined}
      storageKey={THING_FILTER_STORAGE_KEY}
      title={t('terms:thing', { count: T_MANY })}
    />
  );
};
